/**
 * Native status-bar bootstrap mirrors the resolved DOM appearance from
 * index.html/useTheme. Both sapphire and graphite headers need light icons.
 * Keep WebView overlay off; hardware back and App listeners belong to their
 * existing hooks, so this module only observes appearance attributes.
 */
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

// IMPORTANT: Capacitor StatusBar Style values are named by the BACKGROUND
// they're designed for, NOT by the text color they produce. From the official
// docs (https://capacitorjs.com/docs/apis/status-bar):
//   Style.Dark  = "Light text for dark backgrounds"  (light icons)
//   Style.Light = "Dark text for light backgrounds"  (dark icons)
const COLORS = {
  light: { bg: '#123A8C', style: Style.Dark },
  dark: { bg: '#111827', style: Style.Dark },
} as const;

function isDarkPreferred(): boolean {
  // The bootstrap runs before native initialization; absence of .dark means
  // resolved light, including a manual light override while the OS is dark.
  return document.documentElement.classList.contains('dark');
}

async function applyStatusBar(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    // Read after the plugin await so a fast toggle uses the newest header.
    await StatusBar.setBackgroundColor({ color: isDarkPreferred() ? COLORS.dark.bg : COLORS.light.bg });
  } catch (err) {
    console.warn('[native] StatusBar apply failed', err);
  }
}

export async function initNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await applyStatusBar();

    // useTheme resolves both manual and system changes. Observe its result
    // instead of independently resolving the OS and overriding manual light.
    const mo = new MutationObserver(() => void applyStatusBar());
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-resolved-theme'],
    });
  } catch (err) {
    console.warn('[native] StatusBar init failed', err);
  }
}
